import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import { FormContainer, TextFieldElement, useForm } from 'react-hook-form-mui';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';

import { SubstitutionIdInput } from '@barboxx/admin-components';
import { CREATE_SUBSTITUTION_SCHEMA } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { Substitution } from '@barboxx/models';

interface Props {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const CreateSubstitutionDialog = ({
    onClose,
    onSuccess,
    open,
}: Props) => {
    const notifications = useNotifications();

    const formContext = useForm<Substitution.CreateRequest>({
        resolver: yupResolver(CREATE_SUBSTITUTION_SCHEMA),
        defaultValues: {
            targetType: 'ProductType',
            substitutionType: 'ProductType',
        },
    });

    const mutateCreateSubstitution = useMutation({
        mutationKey: ['substitution'],
        mutationFn: (data: Substitution.CreateRequest) =>
            Api.Core.Substitution.postSubstitution(data),
    });

    const onSubmit = async (values: Substitution.CreateRequest) => {
        return mutateCreateSubstitution
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show(`Created ${values.name}!`, {
                    severity: 'success',
                });
                onClose();
                onSuccess();
            })
            .catch(e => {
                notifications.show('Error creating substitution!', {
                    severity: 'error',
                });
            });
    };

    const handleCancelCreate = () => {
        formContext.reset();
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleCancelCreate}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            fullWidth
        >
            <DialogTitle id="alert-dialog-title">
                Create Substitution
            </DialogTitle>
            <FormContainer onSuccess={onSubmit} formContext={formContext}>
                <DialogContent
                    sx={{ gap: 2, display: 'flex', flexDirection: 'column' }}
                >
                    <TextFieldElement fullWidth name="name" label="Name" />
                    <TextFieldElement
                        fullWidth
                        name="description"
                        label="Description"
                    />
                    <SubstitutionIdInput type="target" />

                    <SubstitutionIdInput type="substitution" />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelCreate} color="inherit">
                        Cancel
                    </Button>

                    <Button
                        color="primary"
                        variant="contained"
                        loading={formContext.formState.isSubmitting}
                        type="submit"
                        disabled={formContext.formState.isSubmitting}
                    >
                        Create
                    </Button>
                </DialogActions>
            </FormContainer>
        </Dialog>
    );
};
